import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { TvhTransactionStatus } from '../../admin/constants/transaction-status';
import { LocationState } from '../../location/store/location.state';
import { NavigateService } from '../../shared/services/navigate.service';
import { BasePaginatedTableState, BasePaginatedTableStateModel } from '../../shared/store/base-paginated-table.state';
import { TvhUserTransactionLog } from '../models/user-transaction-log';
import { UserTransactionsApiService } from '../services/user-transactions-api.service';

export interface TransactionsStateModel extends BasePaginatedTableStateModel<TvhUserTransactionLog> {
  filterForm: {
    model: {
      action: TvhTransactionStatus | '';
      dateFrom: string;
      dateTo: string;
    };
    dirty: boolean;
    status: string;
    errors: { [key: string]: unknown };
  };
}

export class Cancel {
  static readonly type = '[UserTransactions] Cancel';
}

export class LoadUserTransactionsPage {
  static readonly type = '[UserTransactions] Load Page';
  constructor(public event?: import('@angular/material/paginator').PageEvent) {}
}

export class SearchUserTransactions {
  static readonly type = '[UserTransactions] Search';
}

export class ResetUserTransactionsFilters {
  static readonly type = '[UserTransactions] Reset Filters';
}

export class ResetUserTransactionsState {
  static readonly type = '[UserTransactions] Reset State';
}

const defaults: TransactionsStateModel = {
  pageItems: [],
  paginatorIndex: 0,
  pageSize: 10,
  totalCount: 0,
  filterForm: {
    model: {
      action: '',
      dateFrom: new Date().toISOString(),
      dateTo: new Date().toISOString(),
    },
    dirty: false,
    status: '',
    errors: {},
  },
};

@State<TransactionsStateModel>({
  name: 'userTransactions',
  defaults: defaults,
})
@Injectable()
export class UserTransactionsState extends BasePaginatedTableState<TvhUserTransactionLog, TransactionsStateModel> {
  searchOnReset = true;

  constructor(
    protected readonly store: Store,
    protected api: UserTransactionsApiService,
    protected readonly navigate: NavigateService,
    private readonly locationState: LocationState,
  ) {
    super(store, 'userTransactions', api, navigate);
  }

  // --- Public API ---

  cancel(): void {
    this.store.dispatch(new Cancel());
  }

  getDataFromBackend(event?: import('@angular/material/paginator').PageEvent): void {
    this.store.dispatch(new LoadUserTransactionsPage(event));
  }

  changePage(event: import('@angular/material/paginator').PageEvent): void {
    this.store.dispatch(new LoadUserTransactionsPage(event));
  }

  resetFilters(): void {
    this.store.dispatch(new ResetUserTransactionsFilters());
  }

  reset(): void {
    this.store.dispatch(new ResetUserTransactionsState());
  }

  search(): void {
    this.store.dispatch(new SearchUserTransactions());
  }

  // --- Action Handlers ---

  @Action(Cancel)
  cancelHandler() {
    if (this.locationState.isLocationConfirmed) {
      this.navigate.toLocationById(this.locationState.location.id);
    } else {
      this.navigate.toLocationSelect();
    }
  }

  @Action(LoadUserTransactionsPage)
  loadUserTransactionsPageHandler(ctx: StateContext<TransactionsStateModel>, action: LoadUserTransactionsPage) {
    return this.fetchPage(action.event).subscribe((page) => {
      ctx.setState(this.withPage(ctx.getState(), page));
    });
  }

  @Action(SearchUserTransactions)
  searchUserTransactionsHandler(ctx: StateContext<TransactionsStateModel>) {
    return this.fetchPage().subscribe((page) => {
      ctx.setState(this.withPage(ctx.getState(), page));
    });
  }

  @Action(ResetUserTransactionsFilters)
  resetUserTransactionsFiltersHandler(ctx: StateContext<TransactionsStateModel>) {
    ctx.setState(defaults);
    return this.fetchPage().subscribe((page) => {
      ctx.setState(this.withPage(ctx.getState(), page));
    });
  }

  @Action(ResetUserTransactionsState)
  resetUserTransactionsStateHandler(ctx: StateContext<TransactionsStateModel>) {
    ctx.setState(defaults);
  }
}
